import { useCallback, useEffect, useRef, useState } from "react";
import type { Client } from "@microsoft/microsoft-graph-client";
import { GRAPH_SCOPES, LeaveSystemConfig } from "../config/leaveSystemConfig";

export interface UseGraphResult {
  graph: Client | null;
  error: string | null;
  /** True once initialization has settled (success, failure, or skipped). */
  ready: boolean;
  /** True in MSAL mode when the user must click Sign in before Graph works. */
  needsSignIn: boolean;
  /** Trigger interactive sign-in (MSAL redirect). */
  signIn: () => void;
  /** Sign out (MSAL redirect logout). No-op in Teams SSO / mock mode. */
  signOut: () => void;
}

/**
 * Acquire a Microsoft Graph client. In "graph" data mode it uses either MSAL
 * browser sign-in (default) or Teams SSO, per REACT_APP_AUTH_MODE. In "mock"
 * mode it resolves immediately with graph = null so the app runs standalone.
 */
export function useGraph(): UseGraphResult {
  const [graph, setGraph] = useState<Client | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const signInRef = useRef<(() => Promise<void>) | null>(null);
  const signOutRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (LeaveSystemConfig.dataMode !== "graph") {
        if (!cancelled) setReady(true);
        return;
      }
      try {
        if (LeaveSystemConfig.authMode === "teams") {
          await initTeams();
        } else {
          await initMsal();
        }
      } catch (err) {
        // Keep named errors ("BrowserAuthError: …"); drop the bare "Error: " prefix.
        const message = err instanceof Error && err.name === "Error" ? err.message : String(err);
        if (!cancelled) setError(message);
      } finally {
        if (!cancelled) setReady(true);
      }
    }

    async function initMsal() {
      // MSAL needs the browser's Web Crypto API, which only exists on https://
      // or localhost. Say so plainly instead of MSAL's "crypto_nonexistent".
      if (!window.isSecureContext) {
        throw new Error(
          `sign-in only works on a secure address, and ${window.location.origin} isn't one. ` +
            `On this PC, open http://localhost:${window.location.port || "53000"}. ` +
            `To let others on the network test, run "npm run dev:lan" and open ` +
            `https://${window.location.hostname}:${window.location.port || "53000"} instead.`,
        );
      }
      const { getMsalGraph } = await import("../services/authClient");
      const { client, signIn, signOut } = await getMsalGraph(
        LeaveSystemConfig.clientId,
        LeaveSystemConfig.tenantId,
      );
      if (cancelled) return;
      signInRef.current = signIn;
      signOutRef.current = signOut;
      if (client) setGraph(client);
      else setNeedsSignIn(true);
    }

    async function initTeams() {
      await import("isomorphic-fetch");
      const teamsfx: any = await import("@microsoft/teamsfx");
      const instance = new teamsfx.TeamsFx();
      const credential = instance.getCredential();
      const client = teamsfx.createMicrosoftGraphClientWithCredential(
        credential,
        GRAPH_SCOPES,
      );
      if (!cancelled) setGraph(client);
    }

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(() => {
    signInRef.current?.().catch((e) => setError(String(e)));
  }, []);

  const signOut = useCallback(() => {
    signOutRef.current?.().catch((e) => setError(String(e)));
  }, []);

  return { graph, error, ready, needsSignIn, signIn, signOut };
}
